import React, { useEffect, useState, useMemo } from 'react';
import { useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
// 1. Импортируем хуки для роутинга (v5)
import { useHistory, useParams } from 'react-router-dom';
import { formulasState, isLoadingState } from '../../../atoms/dataAtoms';
import { physicsApi } from '../../../api';
import { CATEGORIES } from '../../utils/mappings';
import 'katex/dist/katex.min.css';

import FormulasRead from './FormulasRead';
import FormulaNothingFound from './FormulaNothingFound';
import FormulaLoading from './FormulaLoading';

import SEO from '../../../seo';

// --- ИМПОРТ SVG ИКОНОК ---
import { ReactComponent as IconMechanics } from '../../../assets/images/formula_icon0.svg';
import { ReactComponent as IconThermo } from '../../../assets/images/formula_icon1.svg';
import { ReactComponent as IconOptics } from '../../../assets/images/formula_icon2.svg';
import { ReactComponent as IconNuclear } from '../../../assets/images/formula_icon3.svg';
import { ReactComponent as IconElectric } from '../../../assets/images/formula_icon4.svg';
import { ReactComponent as IconMolecular } from '../../../assets/images/formula_icon5.svg';
import { ReactComponent as IconAtomic } from '../../../assets/images/formula_icon6.svg';
import { ReactComponent as IconConstants } from '../../../assets/images/formula_icon7.svg';

const CATEGORY_ICONS = {
    0: IconMechanics,
    1: IconThermo,
    2: IconOptics,
    3: IconNuclear,
    4: IconElectric,
    5: IconMolecular,
    6: IconAtomic,
    7: IconConstants
};

const FormulasFragment = () => {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language ? i18n.language.substring(0, 2) : 'en';

    // 3. Инициализируем роутер
    const history = useHistory();
    const { categoryId, formulaId } = useParams();

    const [formulas, setFormulas] = useRecoilState(formulasState);
    const [loading, setLoading] = useRecoilState(isLoadingState);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);

    const [placeholderText, setPlaceholderText] = useState(t('translate.premium_list3_title'));

    useEffect(() => {
        const fetchData = async () => {
            if (formulas.length > 0) return;
            setLoading(true);
            try {
                const response = await physicsApi.getFormulaList(currentLang);
                setFormulas(response.data);
            } catch (error) {
                console.error("Ошибка загрузки:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [setFormulas, formulas.length, setLoading]);

    useEffect(() => {
        if (formulas.length > 0) {
            const randomIndex = Math.floor(Math.random() * formulas.length);
            const randomFormula = formulas[randomIndex];
            if (randomFormula && randomFormula.content.formulaName) {
                setPlaceholderText(`${randomFormula.content.formulaName}...`);
            }
        }
    }, [formulas]);

    // Фильтрация + Сортировка
    const filteredFormulas = useMemo(() => {
        const filtered = formulas.filter((item) => {
            const name = item.content.formulaName.toLowerCase();
            const query = searchTerm.toLowerCase();
            const matchesSearch = name.includes(query);

            const matchesCategory = selectedCategory === null || item.category === parseInt(selectedCategory);

            return matchesSearch && matchesCategory;
        });

        return filtered.sort((a, b) => {
            return a.content.formulaName.localeCompare(b.content.formulaName, 'ru');
        });

    }, [formulas, searchTerm, selectedCategory]);

    // 4. Ищем формулу для модалки, если в URL есть formulaId
    const activeFormula = useMemo(() => {
        if (!formulaId || formulas.length === 0) return null;
        return formulas.find(f => String(f._id) === String(formulaId));
    }, [formulas, formulaId]);


    const handleClearSearch = () => {
        setSearchTerm('');
        setSelectedCategory(null);
    };

    const handleCategoryClick = (catId) => {
        if (selectedCategory === catId) {
            setSelectedCategory(null);
        } else {
            setSelectedCategory(catId);
            setSearchTerm('');
        }
    };

    // 5. Обработчики открытия и закрытия модалки
    const handleFormulaClick = (item) => {
        let safeColorId = 0;
        if (item.color !== undefined && item.color >= 0 && item.color <= 9) {
            safeColorId = item.color;
        }
        history.push(`/formuls/${safeColorId}/${item._id}`);
    };

    const handleCloseModal = () => {
        history.push('/formuls');
    };

    {/*---LOADER---*/}
    if (loading) {
        return <FormulaLoading />;
    }
    {/*---LOADER---*/}

    {/*---FORMULA-CONTENT---*/}
    return (
        <div className="formulas-page">

            <SEO>
                {/* Формулы – Физика */}
                <title>{`${t('translate.formuls_title')}: ${t('translate.app_name2')}`}</title>
            </SEO>

            {/* --- TOOLBAR --- */}
            <div className="toolbar">
                <div className="toolbar__header">
                    <h1 className="toolbar__title">{t('translate.app_name2')}</h1>
                    <span className="toolbar__subtitle_formuls">{t('translate.formuls_title')}</span>
                </div>

                <div className="toolbar__search-wrapper">
                    <input
                        type="text"
                        className="toolbar__input"
                        placeholder={placeholderText}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />

                    {searchTerm && (
                        <button
                            className='toolbar__reset-button'
                            onClick={handleClearSearch}
                            title={t('translate.search_clear') || "Очистить"}
                        />
                    )}
                </div>
            </div>

            {/* --- СЕТКА КАТЕГОРИЙ --- */}
            {!searchTerm && (
                <div className="categories-grid">
                    {Object.keys(CATEGORIES).map(key => {
                        const catId = parseInt(key);
                        const cat = CATEGORIES[key];
                        const IconComponent = CATEGORY_ICONS[cat.id];

                        const isSelected = selectedCategory === catId;
                        const isOtherSelected = selectedCategory !== null && !isSelected;

                        return (
                            <div
                                key={key}
                                className={`category-card category-card--${cat.id}`}
                                onClick={() => handleCategoryClick(catId)}
                                style={{
                                    opacity: isOtherSelected ? 0.4 : 1,
                                    transform: isSelected ? 'scale(1.02)' : 'none',
                                    border: isSelected ? `2px solid currentColor` : ''
                                }}
                            >
                                <div className="category-card__content">
                                    <div className="category-card__title">
                                        {t(cat.titleKey)}
                                    </div>
                                    <div className="category-card__desc">
                                        {t(cat.descKey)}
                                    </div>
                                </div>

                                <div className="category-card__icon">
                                    {IconComponent && <IconComponent />}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* --- СПИСОК ФОРМУЛ --- */}
            {filteredFormulas.length > 0 ? (
                <div className="formulas-grid">
                    {filteredFormulas.map((item) => {
                        const firstLetter = item.content.formulaName
                            ? item.content.formulaName.charAt(0).toUpperCase()
                            : '?';

                        const colorId = item.color;
                        const colorClassSuffix = colorId !== undefined ? colorId : 'default';

                        return (
                            <div
                                key={item._id}
                                className={`formula-card formula-card--${colorClassSuffix}`}
                                // 6. Вешаем обработчик клика
                                onClick={() => handleFormulaClick(item)}
                            >
                                <div className="formula-card__icon">
                                    {firstLetter}
                                </div>

                                <div className="formula-card__content">
                                    <h3 className="formula-card__title">{item.content.formulaName}</h3>
                                    <p className="formula-card__desc">{item.content.description}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                // --- НИЧЕГО НЕ НАЙДЕНО ---
                <FormulaNothingFound searchTerm={searchTerm} />
            )}

            {/* --- READ-FORMULAS --- */}
            {activeFormula && (
                <FormulasRead
                    formula={activeFormula}
                    categoryId={categoryId}
                    onClose={handleCloseModal}
                />
            )}


            {/* ============================== КОПИРАЙТ ============================== */}
            <div className="settings-footer" style={{marginTop: '40px'}}>
                <p>© <a href='https://chernykh.tech/' target='_blank' rel='noreferrer'>chernykh.tech</a> / All rights reserved, {new Date().getFullYear()}</p>
            </div>

        </div>
    );
    {/*---FORMULA-CONTENT---*/}
};

export default FormulasFragment;